import React from 'react';
import { Table } from 'reactstrap';
import dayjs from 'dayjs';

const FormattedCurrency = ({ value }) => {
  if (value === null || value === undefined) return "$0.00";
  const num = parseFloat(value);
  if (isNaN(num)) return "$0.00";

  const isNegative = num < 0;
  const formatted = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(Math.abs(num));

  return isNegative ? `(${formatted})` : formatted;
};

const AccountTransactionsReport = ({ data, reportInfo }) => {
  const accounts = data?.accounts || [];

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return dayjs(dateStr).format('MM/DD/YYYY');
  };

  return (
    <div style={{ backgroundColor: '#fff', color: '#000', padding: '20px', fontFamily: 'Arial, sans-serif' }}>
      <style>
        {`
          .report-table { width: 100%; border-collapse: collapse; font-size: 12px; }
          .report-table th, .report-table td { padding: 4px; border: none; }
          .report-header { background-color: #2b7bb9; color: white; font-weight: bold; }
          .group-header { color: black; font-weight: bold; font-size: 11px; padding: 4px; border-bottom: 2px solid #ccc; }
          .sub-raw { font-size: 11px; }
          
          .summary-table { width: 50%; margin: 20px auto; border-collapse: collapse; font-size: 12px; }
          .summary-table th, .summary-table td { padding: 6px 12px; border: 1px solid #ddd; text-align: right; }
          .summary-table th { background-color: #f8f9fa; }
          .summary-table tr:last-child { font-weight: bold; }
          
          .page-break { page-break-after: always; padding-bottom: 40px; margin-bottom: 40px; border-bottom: 2px dashed #eee; }
          
          @media print {
            .page-break {
              border-bottom: none;
            }
          }
        `}
      </style>

      {accounts.map((account, index) => {
        const transactions = account.transactions || [];

        // Calculate Summary for this account
        const summary = {
          'Cash or Cash Equivalent': { count: 0, sum: 0 },
          'Check or Check Equivalent': { count: 0, sum: 0 },
          'EPS CC/ACH': { count: 0, sum: 0 },
          'Other CC/ACH': { count: 0, sum: 0 },
          'Other': { count: 0, sum: 0 },
          'Unknown': { count: 0, sum: 0 },
        };

        transactions.forEach(row => {
          const form = row.payment_form?.toLowerCase() || '';
          const collected = parseFloat(row.collected) || 0;

          if (form.includes('cash')) {
            summary['Cash or Cash Equivalent'].count += 1;
            summary['Cash or Cash Equivalent'].sum += collected;
          } else if (form.includes('check')) {
            summary['Check or Check Equivalent'].count += 1;
            summary['Check or Check Equivalent'].sum += collected;
          } else if (form.includes('eps')) {
            summary['EPS CC/ACH'].count += 1;
            summary['EPS CC/ACH'].sum += collected;
          } else if (form.includes('card') || form.includes('cc') || form.includes('ach')) {
            summary['Other CC/ACH'].count += 1;
            summary['Other CC/ACH'].sum += collected;
          } else if (form) {
            summary['Other'].count += 1;
            summary['Other'].sum += collected;
          } else {
            summary['Unknown'].count += 1;
            summary['Unknown'].sum += collected;
          }
        });

        const totalCount = Object.values(summary).reduce((acc, curr) => acc + curr.count, 0);
        const totalSum = Object.values(summary).reduce((acc, curr) => acc + curr.sum, 0);

        // Attempting to extract what we can from calculus. Fallbacks included if data is sparse.
        let customerName = 'Unknown Customer';
        if (account.calculus?.customer?.name) customerName = account.calculus.customer.name;
        else if (account.calculus?.customer_name) customerName = account.calculus.customer_name;

        let stockNo = 'Unknown Stock';
        if (account.calculus?.vehicle?.stock_number) stockNo = account.calculus.vehicle.stock_number;
        else if (account.calculus?.stock_no) stockNo = account.calculus.stock_no;

        let vehicleDesc = '';
        if (account.calculus?.vehicle) {
          vehicleDesc = `${account.calculus.vehicle.year || ''} ${account.calculus.vehicle.make || ''} ${account.calculus.vehicle.model || ''}`;
        }

        const groupKey = `Acct Desc: Acct ID/Loan#: ${account.id}/${account.act_number || account.booking_id || ''} | ${customerName} | Stock#: ${stockNo} ${vehicleDesc}`.trim();

        return (
          <div key={account.id || index} className={index !== accounts.length - 1 ? "page-break" : ""}>
            <div className="d-flex justify-content-between mb-3">
              <div>
                <h4 style={{ margin: 0, fontWeight: 'bold' }}>iDMS Account Transactions</h4>
                <div style={{ fontWeight: 'bold', marginTop: '4px' }}>Page {index + 1}</div>
              </div>
              <div className="text-right">
                <div style={{ fontWeight: 'bold' }}>Executed By: {typeof reportInfo?.owner === 'object' ? (reportInfo.owner.username || reportInfo.owner.full_name || 'System') : (reportInfo?.owner || 'System')}</div>
                <div style={{ fontWeight: 'bold' }}>On {dayjs().format('MM/DD/YYYY hh:mm A')}</div>
              </div>
            </div>

            <table className="report-table mb-4">
              <thead>
                <tr className="report-header">
                  <th style={{ width: '10%' }}>Receipt #</th>
                  <th style={{ width: '10%' }}>Post Date</th>
                  <th style={{ width: '12%' }}></th>
                  <th style={{ width: '12%', textAlign: 'center' }}></th>
                  <th style={{ width: '10%', textAlign: 'right' }}></th>
                  <th style={{ width: '10%', textAlign: 'right' }}></th>
                  <th style={{ width: '12%', textAlign: 'right' }}></th>
                  <th style={{ width: '24%' }}>Form - Ref</th>
                </tr>
                <tr className="report-header">
                  <th>Created By</th>
                  <th>Effective Date</th>
                  <th>Beg Balance</th>
                  <th style={{ textAlign: 'center' }}>Total Collected</th>
                  <th style={{ textAlign: 'right' }}>Adjusted</th>
                  <th style={{ textAlign: 'right' }}>Rebated</th>
                  <th style={{ textAlign: 'right' }}>End Balance</th>
                  <th>Description</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td colSpan="8" className="group-header">{groupKey}</td>
                </tr>
                {transactions.length > 0 ? transactions.map((row) => (
                  <React.Fragment key={row.id || Math.random()}>
                    {/* First Line */}
                    <tr className="sub-raw" style={{ borderTop: '1px solid #eaeaea' }}>
                      <td style={{ fontWeight: 'bold' }}>{row.receipt_id || ''}</td>
                      <td style={{ fontWeight: 'bold' }}>{formatDate(row.createdAt)}</td>
                      <td></td>
                      <td></td>
                      <td></td>
                      <td></td>
                      <td></td>
                      <td style={{ fontWeight: 'bold' }}>
                        {row.payment_form || ''} {row.ref_number ? `- ${row.ref_number}` : ''}
                      </td>
                    </tr>
                    {/* Second Line */}
                    <tr className="sub-raw">
                      <td>{row.created_user?.username || ''}</td>
                      <td>{formatDate(row.date)}</td>
                      <td><FormattedCurrency value={row.beg_balance} /></td>
                      <td style={{ textAlign: 'center', color: row.collected < 0 ? 'red' : 'inherit' }}>
                        <FormattedCurrency value={row.collected} />
                      </td>
                      <td style={{ textAlign: 'right' }}><FormattedCurrency value={row.adjusted} /></td>
                      <td style={{ textAlign: 'right' }}><FormattedCurrency value={row.rebated || 0} /></td>
                      <td style={{ textAlign: 'right' }}><FormattedCurrency value={row.end_balance} /></td>
                      <td>{row.description || (row.payment_type ? `${row.payment_type.replace(/_/g, ' ')} ${row.note || ''}` : '')}</td>
                    </tr>
                  </React.Fragment>
                )) : (
                  <tr>
                    <td colSpan="8" className="text-center py-3">No transactions found for this account.</td>
                  </tr>
                )}
              </tbody>
            </table>

            <div style={{ backgroundColor: '#2b7bb9', color: 'white', fontWeight: 'bold', padding: '4px', fontSize: '11px', marginBottom: '10px' }}>
              {groupKey}
            </div>

            <table className="summary-table">
              <thead>
                <tr>
                  <th style={{ textAlign: 'right' }}>Payment Form Category</th>
                  <th style={{ textAlign: 'right' }}># of Pymts</th>
                  <th style={{ textAlign: 'right' }}>Sum of Pymts</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(summary).map(([category, stats]) => (
                  <tr key={category}>
                    <td>{category}</td>
                    <td>{stats.count}</td>
                    <td><FormattedCurrency value={stats.sum} /></td>
                  </tr>
                ))}
                <tr>
                  <td>GRAND TOTALS</td>
                  <td>{totalCount}</td>
                  <td><FormattedCurrency value={totalSum} /></td>
                </tr>
              </tbody>
            </table>
          </div>
        );
      })}
    </div>
  );
};

export default AccountTransactionsReport;
